import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link, navigateTo } from 'navigation'
import { Button } from 'components/UIElements'
import Loader from 'components/D3Loader'
import { INSTRUMENT_EVENTS_MAP } from 'utils/constants'
import {
  getIcon,
  formatSubmissionSurvey
} from '../../WebSurveys/utils/instrumentUtils'
import { clockIcon, noActivitiesHumi } from '../../../assets/assets'
import {
  getInProgressInstrumentForActivities,
  getCompletedInstanceKeys,
  removeInstrumentFromLocalStorage,
  canResumeInProgressInstrument,
  resolveActiveInstanceIdForInstrument,
} from '../utils/instrumentUtils'
import { getExpirationText } from '../utils/expirationUtils'
import { logSentryEvent, SENTRY_MESSAGES } from '../../../utils/sentry'
import InstrumentTitle from './InstrumentTitle'
import { resolveInstrumentWebSurveyPath } from '../../WebSurveys/utils/webSurveyUrl'

const parseLocalizedTitle = ({ selectedLocale, instrument }) => {

  const { title, displayNameTranslations } = instrument
  if (!displayNameTranslations) return title

  const formattedSelectedLocale = selectedLocale.toLowerCase()

  const localizedDisplayName = displayNameTranslations[formattedSelectedLocale]

  if (localizedDisplayName) return localizedDisplayName

  const locale = formattedSelectedLocale.includes('_')
    ? formattedSelectedLocale.split('_')[0]
    : formattedSelectedLocale

  return displayNameTranslations[locale] || title
}

const PendingInstrumentsList = ({
  loading,
  selectedLocale,
  sendEvent,
  setSubmissionSurvey,
  strings,
  displayingList,
  humiIsabellaDisabled,
  participantId,
  allInstances = [],
}) => {
  const completedInstanceKeys = useMemo(
    () => getCompletedInstanceKeys(allInstances),
    [allInstances],
  )

  useEffect(() => {
    displayingList.forEach((instrument) => {
      const identity = instrument.identity
      const instanceId = resolveActiveInstanceIdForInstrument(instrument)
      const storedInstrument = getInProgressInstrumentForActivities({
        instrument,
        participantId,
        instanceId,
      })
      if (
        storedInstrument?.instance_id &&
        completedInstanceKeys.has(
          `${identity}:${storedInstrument.instance_id}`,
        )
      ) {
        removeInstrumentFromLocalStorage({
          instrumentId: identity,
          participantId,
          instanceId: storedInstrument.instance_id,
        })
      }
    })
  }, [displayingList, participantId, completedInstanceKeys])

  const getStatusText = () => {
    return 'In Progress'
  }

  return (
    <div className='pending-instruments'>
      {loading && <Loader inContainer />}
      {!loading &&
        (displayingList.length > 0 ? (
          displayingList.map(instrument => {
            const {
              identity,
              displayKey,
              display_icon,
              type,
              depQId,
              schedule,
              instances,
              visibility
            } = instrument
            const instanceId = resolveActiveInstanceIdForInstrument(instrument)
            const submissionSurvey = formatSubmissionSurvey({
              ...instrument,
              instance_id: instanceId,
            })
            const storedInstrument = getInProgressInstrumentForActivities({
              instrument,
              participantId,
              instanceId,
            })

            const instrumentId = identity
            const isStoredInstanceCompleted =
              storedInstrument?.instance_id &&
              completedInstanceKeys.has(
                `${instrumentId}:${storedInstrument.instance_id}`,
              )
            const canBeResumed =
              !isStoredInstanceCompleted &&
              canResumeInProgressInstrument(instrument, storedInstrument)

            const expirationText = getExpirationText(schedule, instances, visibility);
            const surveyUrl = resolveInstrumentWebSurveyPath(instrument)

            return (
              <Link
                to={surveyUrl}
                key={`link-${displayKey || identity}`}
                onClick={e => {
                  e.stopPropagation()
                  logSentryEvent({
                    message: SENTRY_MESSAGES.instrumentOpened,
                    tags: {
                      flow: 'instrument',
                      action: 'opened',
                      instrument_id: instrumentId != null ? String(instrumentId) : undefined,
                      instance_id: instanceId != null ? String(instanceId) : undefined,
                      instrument_type: type,
                      'activities.mode': 'carousel',
                      'submission.action': canBeResumed ? 'resume' : 'start',
                    },
                    breadcrumb: {
                      category: 'instrument',
                      message: `Instrument Opened ${canBeResumed ? 'resume' : 'start'}`,
                    },
                  })
                  setSubmissionSurvey(
                    canBeResumed ? storedInstrument : submissionSurvey
                  )
                }}>
                {canBeResumed ? (
                  <div className='status-badge'>
                    <div className='status-badge-text'>{getStatusText()}</div>
                  </div>
                ) : null}
                <img src={getIcon(display_icon)} alt='display-icon' />
                <div>
                  <div className='instrument-status-box'>
                    <div className={`type-tag ${type.toLowerCase()}`}>
                      {strings[type.toLowerCase()]}
                    </div>
                    {expirationText && (
                      <div className='expiration-tag'>
                        <div className='expiration-icon'>{clockIcon()}</div>
                        <div className='expiration-text'>
                          {expirationText}
                        </div>
                      </div>
                    )}
                  </div>
                  <InstrumentTitle
                    text={parseLocalizedTitle({ selectedLocale, instrument })}
                  />
                </div>
                {canBeResumed ? (
                  <Button
                    type='button'
                    className='resume-button'
                    content={'Resume'}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      logSentryEvent({
                        message: SENTRY_MESSAGES.instrumentOpened,
                        tags: {
                          flow: 'instrument',
                          action: 'opened',
                          instrument_id: instrumentId != null ? String(instrumentId) : undefined,
                          instance_id: instanceId != null ? String(instanceId) : undefined,
                          instrument_type: type,
                          'activities.mode': 'carousel',
                          'submission.action': 'resume',
                        },
                        breadcrumb: {
                          category: 'instrument',
                          message: 'Instrument Opened resume',
                        },
                      })
                      setSubmissionSurvey(
                        canBeResumed ? storedInstrument : submissionSurvey
                      )
                      if (surveyUrl) navigateTo(surveyUrl)
                    }}
                  />
                ) : null}
              </Link>
            )
          })
        ) : (
          <div className='no-activities'>
            {!humiIsabellaDisabled ? (
              <img src={noActivitiesHumi} alt='no-activities-hui' />
            ) : null}
            <p>{strings.activities_completed_all}</p>
            <p>{strings.activities_check_back}</p>
          </div>
        ))}
    </div>
  )
}

export default PendingInstrumentsList

PendingInstrumentsList.propTypes = {
  loading: PropTypes.bool,
  pendingInstruments: PropTypes.arrayOf(
    PropTypes.shape({
      depQId: PropTypes.number,
      display_icon: PropTypes.string
    })
  ),
  instruments: PropTypes.arrayOf(
    PropTypes.shape({
      depQId: PropTypes.number,
      display_icon: PropTypes.string
    })
  ),
  selectedLocale: PropTypes.string,
  sendEvent: PropTypes.func,
  setSubmissionSurvey: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  allInstances: PropTypes.array,
}
